import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Chip, Divider, IconButton, InputAdornment, Paper, Stack,
    Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import dayjs from "dayjs";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import MeetingRoomRoundedIcon from "@mui/icons-material/MeetingRoomRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

import BaseCalendar from "../../component/calendar/BaseCalendar";
import FormModal from "../../component/common/FormModal";
import FormField from "../../component/common/FormField";
import ComboBox from "../../component/common/ComboBox";
import ActionButton from "../../component/common/ActionButton";
import LoadingDots from "../../component/common/LoadingDots";
import EmployeeAutocomplete from "../../component/common/EmployeeAutocomplete";
import AppAutocomplete from "../../component/common/AppAutocomplete";
import { meetingRoomApi } from "./api/meetingRoomApi";
import { useMyPermissions } from "../../util/useMyPermissions";
import { runApiAction } from "../../util/apiFeedback";
import { confirm as confirmDialog } from "../../util/confirmDialog";
import { ds } from "../../styles/designSystem";

const C = ds.colors;   // 색상은 디자인 토큰에서만 (하드코딩 금지)

/* ─────────────────────────────────────────────────────────────────────────────
 * 시간표 눈금 상수
 *
 * 10분은 <b>화면의 눈금</b>이지 서버의 제약이 아니다(2026-08-13). 서버는 분 단위까지 받아들이므로
 * 여기 값을 바꿔도 저장이 거절되지 않는다 — 예전에는 10분 배수가 아니면 서버가 400 을 던져서
 * "예약 가능이라더니 저장이 안 된다" 가 됐다.
 *
 * 눈금을 10분으로 유지하는 이유는 편의다 — 기본값·빈 칸 클릭·시각 입력 스피너가 10분씩 맞물려
 * 대부분의 회의를 두세 번의 조작으로 잡을 수 있다. 그러고도 분 자리를 직접 타이핑하면
 * 10:05 같은 시각이 그대로 저장된다.
 *
 * 10분을 한 칸씩 그리면 하루가 84칸이라 눈이 못 따라간다. 그래서 눈금은 '한 시간 = 고정 높이' 로
 * 두고 예약 블록만 분 단위로 비례 배치한다 — 10분짜리도, 5분짜리도 정확한 위치·길이로 보인다.
 * ────────────────────────────────────────────────────────────────────────── */
const SLOT_MIN = 10;
const HOUR_H = 56;                 // 1시간 높이(px)
const PX_PER_MIN = HOUR_H / 60;
const MIN_BLOCK_H = 18;            // 10분짜리도 클릭·글자가 보이도록 하는 최소 높이
const DEFAULT_START_HOUR = 8;      // 기본으로 보여줄 시간창 (그 밖의 예약이 있으면 자동으로 넓어진다)
const DEFAULT_END_HOUR = 22;
const DEFAULT_DURATION_MIN = 60;   // 빈 칸을 클릭해 새로 잡을 때의 기본 길이

/** 회의실별 색 — 등록 순서(index)로 배정. 시간표 블록·달력 배지·범례가 같은 색을 쓴다. */
const ROOM_PALETTE = [
    { bg: "#eff6ff", bar: "#2563eb", text: "#1e40af" },
    { bg: "#f0fdf4", bar: "#16a34a", text: "#15803d" },
    { bg: "#fff7ed", bar: "#ea580c", text: "#c2410c" },
    { bg: "#faf5ff", bar: "#9333ea", text: "#6d28d9" },
    { bg: "#fdf2f8", bar: "#db2777", text: "#be185d" },
    { bg: "#ecfeff", bar: "#0891b2", text: "#0e7490" },
];
const PAST_COLOR = { bg: "#f1f5f9", bar: "#cbd5e1", text: "#94a3b8" };   // 이미 끝난 회의 = 회색

const D = "YYYY-MM-DD";
const clampHour = (h) => Math.max(0, Math.min(24, h));
const pad2 = (n) => String(n).padStart(2, "0");
const hhmm = (v) => dayjs(v).format("HH:mm");

/** 폼의 datetime-local 값 형식 — 날짜·시각을 한 칸에서 고른다(따로 두면 둘이 어긋나기 쉽다). */
const DT = "YYYY-MM-DDTHH:mm";
/** datetime-local 값("YYYY-MM-DDTHH:mm") → 서버가 받는 LocalDateTime 문자열(초 포함). */
const toServerDateTime = (v) => (v && v.length === 16 ? `${v}:00` : v);

/** 10분 눈금으로 내림 정렬 — 기본값·빈 칸 클릭을 눈금에 맞춘다(서버 제약이 아니라 편의). */
const snapDown = (t) => t.startOf("hour").add(Math.floor(t.minute() / SLOT_MIN) * SLOT_MIN, "minute");

/** 예약이 하루(day)에 걸치는 구간을 [분, 분] 으로. 여러 날 예약은 그 날 부분만 잘라 낸다. */
function daySpanMinutes(resv, day) {
    const dayStart = day.startOf("day");
    const dayEnd = dayStart.add(1, "day");
    const s = dayjs(resv.startAt).isBefore(dayStart) ? dayStart : dayjs(resv.startAt);
    const e = dayjs(resv.endAt).isAfter(dayEnd) ? dayEnd : dayjs(resv.endAt);
    return [s.diff(dayStart, "minute"), e.diff(dayStart, "minute")];
}

/** 예약이 걸친 모든 날짜 키. 종료는 반열림 [start, end) 이라 1분 빼고 센다(자정 종료가 다음날로 새지 않게). */
function coveredDays(resv) {
    const out = [];
    let d = dayjs(resv.startAt).startOf("day");
    const last = dayjs(resv.endAt).subtract(1, "minute").startOf("day");
    // 데이터가 깨져 endAt <= startAt 인 경우에도 최소 하루는 잡아 화면에서 사라지지 않게 한다.
    if (last.isBefore(d)) return [d.format(D)];
    while (!d.isAfter(last)) { out.push(d.format(D)); d = d.add(1, "day"); }
    return out;
}

const emptyRoomDraft = () => ({ id: null, name: "", location: "", capacity: "", memo: "", active: true });

/**
 * 회의실 예약 — 월간 달력에서 날짜를 고르면 그 날의 회의실별 시간표가 열린다.
 *
 * <p>비품 예약과 나란한 화면이지만 통제는 다르다 — 예약은 전 직원이 하되
 * <b>수정·삭제는 예약자 본인(또는 관리자)</b> 만 가능하다. 서버가 최종 판정하며(`editable`),
 * 화면은 그 값에 따라 버튼을 감춘다.
 */
export default function MeetingRoomPage() {
    const { hasPerm } = useMyPermissions();
    const canManage = hasPerm("MEETING_ROOM_MANAGE");

    const [anchor, setAnchor]       = useState(dayjs());
    const [selectedDay, setSelDay]  = useState(dayjs());
    /**
     * 회의실은 <b>사용 안 함 포함 전체</b>를 들고 있다가 쓰임새별로 나눠 쓴다.
     *
     * <p>활성만 받아 오면 관리 화면에서 '사용 안 함'으로 내린 회의실이 목록에서 통째로 사라져
     * <b>다시 켤 방법이 없어진다</b>(실제로 그렇게 잠겼다). 관리 모달은 전체를, 시간표·예약 폼은
     * 활성만 본다.
     */
    const [allRooms, setAllRooms]   = useState([]);
    const rooms = useMemo(() => allRooms.filter(r => r.active), [allRooms]);
    const [reservations, setResvs]  = useState([]);   // 보이는 달 ±1주만 (전체 로드 안 함)
    const [departments, setDepts]   = useState([]);
    const [me, setMe]               = useState(null);
    const [loading, setLoading]     = useState(true);

    const [form, setForm]           = useState(null);   // null = 닫힘 (예약 생성/상세)
    const [avail, setAvail]         = useState(null);   // 선택 시간대 가용성 { available, conflicts }
    const [roomAdminOpen, setRoomAdminOpen] = useState(false);
    const [saving, setSaving]       = useState(false);
    const timelineScrollRef         = useRef(null);   // 시간표 스크롤 컨테이너 (DayTimeline 이 위치를 맞춘다)

    // ─── 로드 ────────────────────────────────────────────────────
    const loadMasters = useCallback(() => {
        setLoading(true);
        Promise.allSettled([
            meetingRoomApi.getRooms(true), meetingRoomApi.getDepartments(), meetingRoomApi.getMe(),
        ]).then(([r, d, m]) => {
            if (r.status === "fulfilled") setAllRooms(r.value || []);
            if (d.status === "fulfilled") setDepts(d.value || []);
            if (m.status === "fulfilled") setMe(m.value || null);
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => { loadMasters(); }, [loadMasters]);

    // 캘린더는 보이는 달 ±1주만 조회 — 예약이 쌓여도 화면이 무거워지지 않는다.
    const loadReservations = useCallback(() => {
        const from = anchor.startOf("month").subtract(7, "day").format(D);
        const to   = anchor.endOf("month").add(7, "day").format(D);
        meetingRoomApi.getReservations(from, to).then(d => setResvs(d || [])).catch(() => {});
    }, [anchor]);
    useEffect(() => { loadReservations(); }, [loadReservations]);

    const reloadRooms = useCallback(() => {
        meetingRoomApi.getRooms(true).then(d => setAllRooms(d || [])).catch(() => {});
    }, []);

    /**
     * 달을 넘기면 선택 날짜도 따라간다 — 안 그러면 8월 달력을 보면서 우측 시간표는 7월 어느 날이라
     * 두 화면이 서로 다른 이야기를 한다. 이동한 달이 이번 달이면 오늘, 아니면 1일로 맞춘다.
     */
    const onAnchorChange = useCallback((next) => {
        setAnchor(next);
        setSelDay(prev => prev.isSame(next, "month")
            ? prev
            : (dayjs().isSame(next, "month") ? dayjs() : next.startOf("month")));
    }, []);

    // ─── 파생 ────────────────────────────────────────────────────
    // 색은 '전체 목록' 기준 순번으로 정한다 — 활성 목록 기준이면 회의실 하나를 사용 안 함으로 내릴 때
    // 뒤 회의실들의 색이 전부 밀려, 어제와 오늘 같은 방이 다른 색으로 보인다.
    const roomColor = useCallback((roomId) => {
        const idx = allRooms.findIndex(r => String(r.id) === String(roomId));
        return ROOM_PALETTE[(idx < 0 ? 0 : idx) % ROOM_PALETTE.length];
    }, [allRooms]);

    /** 날짜키 → 그 날 예약들(시작 시각 순). 여러 날 예약은 걸친 날마다 들어간다. */
    const byDay = useMemo(() => {
        const map = new Map();
        for (const r of reservations) {
            for (const key of coveredDays(r)) {
                if (!map.has(key)) map.set(key, []);
                map.get(key).push(r);
            }
        }
        for (const list of map.values()) {
            list.sort((a, b) => dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf()
                || String(a.roomName).localeCompare(String(b.roomName)));
        }
        return map;
    }, [reservations]);

    const dayResvs = useMemo(
        () => byDay.get(selectedDay.format(D)) || [], [byDay, selectedDay]);

    // ─── 예약 폼 ─────────────────────────────────────────────────
    const openNew = (roomId, start) => {
        // 시간표 빈 칸을 클릭했으면 그 시각, 상단 [예약하기] 면 선택한 날짜의 '지금' 시각에서 시작.
        const s = snapDown(start || selectedDay.hour(dayjs().hour()).minute(dayjs().minute()));
        const e = s.add(DEFAULT_DURATION_MIN, "minute");
        setAvail(null);
        setForm({
            id: null, editable: true,
            roomId: roomId || (rooms[0]?.id ?? ""),
            title: "",
            startAt: s.format(DT), endAt: e.format(DT),
            depts: [], employees: [], memo: "",
            applicantName: me?.name || "",
        });
    };

    const openDetail = (r) => {
        setAvail(null);
        setForm({
            id: r.id, editable: Boolean(r.editable),
            roomId: r.roomId || "",
            title: r.title || "",
            startAt: dayjs(r.startAt).format(DT), endAt: dayjs(r.endAt).format(DT),
            // 팀 인원 수는 예약에 저장돼 있지 않다 — 부서 목록(live)에서 현재 값을 붙여 준다.
            depts: (r.participants || []).filter(p => p.kind === "DEPARTMENT").map(p => ({
                deptId: p.deptId, deptName: p.name,
                memberCount: departments.find(d => String(d.deptId) === String(p.deptId))?.memberCount ?? 0,
            })),
            // 직원이 삭제된 참여자는 employeeId 가 비어 이름 스냅샷만 남는다 — 자동완성 값으로는
            // 다룰 수 없으므로(선택 해제·중복 판정이 id 기준) 편집 목록에서 뺀다.
            // 읽기 전용 상세에는 deletedNames 로 이름만 그대로 보여 준다.
            employees: (r.participants || []).filter(p => p.kind === "EMPLOYEE" && p.employeeId != null)
                .map(p => ({ id: p.employeeId, name: p.name })),
            deletedNames: (r.participants || [])
                .filter(p => p.kind === "EMPLOYEE" && p.employeeId == null)
                .map(p => p.name).filter(Boolean),
            headcount: r.headcount ?? null, memo: r.memo || "",
            applicantId: r.applicantId ?? null,
            applicantName: r.applicantName || "",
            roomName: r.roomName, deptName: r.deptName,
        });
    };

    const setF = (patch) => setForm(f => ({ ...f, ...patch }));

    /**
     * 시작을 옮기면 종료도 <b>회의 길이를 유지한 채</b> 같이 움직인다.
     * 안 그러면 다음 주로 옮길 때마다 종료를 다시 골라야 하고, 종료가 시작보다 앞서는
     * 잘못된 상태를 한 번 거치게 된다.
     */
    const onStartChange = (v) => setForm(f => {
        if (!v) return { ...f, startAt: v };
        const prevStart = dayjs(f.startAt), prevEnd = dayjs(f.endAt);
        const keepMin = prevStart.isValid() && prevEnd.isValid() && prevEnd.isAfter(prevStart)
            ? prevEnd.diff(prevStart, "minute") : DEFAULT_DURATION_MIN;
        return { ...f, startAt: v, endAt: dayjs(v).add(keepMin, "minute").format(DT) };
    });

    const startAt = form ? toServerDateTime(form.startAt) : null;
    const endAt   = form ? toServerDateTime(form.endAt) : null;
    const rangeValid = Boolean(form?.startAt && form?.endAt)
        && dayjs(form.endAt).isAfter(dayjs(form.startAt));

    // 선택한 회의실·시간대가 비었는지 서버에 실시간으로 묻는다(저장 눌러서야 알게 되지 않도록).
    const availKey = form && form.roomId && rangeValid
        ? `${form.roomId}|${startAt}|${endAt}|${form.id || ""}` : "";
    useEffect(() => {
        // 조건이 바뀌면 직전 결과부터 버린다 — 회의실을 바꾼 순간 옛 '예약 가능' 이 남아 있으면
        // 실제로는 겹치는데 저장 버튼이 열려 있는 상태가 된다.
        setAvail(null);
        if (!availKey) return;
        let alive = true;
        const [roomId, s, e, id] = availKey.split("|");
        meetingRoomApi.getAvailability(roomId, s, e, id || undefined)
            .then(d => { if (alive) setAvail(d || null); })
            // 조회 자체가 실패했으면(네트워크 등) 저장을 막지 않는다 — 겹침의 최종 판정은 어차피 서버다.
            // 여기서 막아 버리면 조회 한 번 실패에 예약이 통째로 잠긴다.
            .catch(() => { if (alive) setAvail({ available: true, conflicts: [], failed: true }); });
        return () => { alive = false; };
    }, [availKey]);

    /**
     * 참석 인원 — <b>참석자 명단에서 자동 계산</b>한다(사용자가 숫자를 직접 적지 않는다).
     * 팀은 재직 인원(memberCount)으로 펼쳐 세고 개인 지명과 더한다.
     *
     * <p>여기 값은 <b>화면용 미리보기</b>다 — 팀원인 사람을 개인으로도 고르면 여기서는 두 번 세지지만,
     * 저장 시 서버가 실제 직원 id 로 중복을 제거해 확정한다(resolveHeadcount). 그래서 저장 뒤
     * 숫자가 줄어들 수 있고, 그게 맞는 값이다.
     */
    const derivedHeadcount = form
        ? (form.employees || []).length
          + (form.depts || []).reduce((sum, d) => sum + (Number(d?.memberCount) || 0), 0)
        : 0;
    const selectedRoom = form ? rooms.find(r => String(r.id) === String(form.roomId)) : null;
    /**
     * 회의실 선택지 — 사용 중인 회의실 + (수정 중인 예약이 잡고 있는 '사용 안 함' 회의실).
     * 관리자가 회의실을 내리면 그 방의 기존 예약은 목록에 없는 id 를 들고 있어, 그대로 두면
     * 드롭다운이 빈칸으로 보이면서 무엇이 잡혀 있는지 알 수 없게 된다.
     */
    const roomOptions = useMemo(() => {
        const opts = rooms.map(r => ({
            value: r.id, label: `${r.name}${r.capacity ? ` (${r.capacity}인)` : ""}`,
        }));
        if (form?.roomId && !rooms.some(r => String(r.id) === String(form.roomId)))
            opts.push({ value: form.roomId, label: `${form.roomName || "회의실"} (사용 안 함)` });
        return opts;
    }, [rooms, form?.roomId, form?.roomName]);
    // 정원 초과는 '경고'만 한다 — 서서 듣는 회의도 있어서 저장까지 막을 일은 아니다.
    const overCapacity = Boolean(selectedRoom?.capacity && derivedHeadcount > selectedRoom.capacity);

    // 내가 예약자가 아닌데 수정 화면이 열렸다 = 관리자 권한으로 남의 회의를 여는 상황.
    const othersReservation = Boolean(form?.id && form.editable && me?.id
        && form.applicantId && String(form.applicantId) !== String(me.id));

    // 가용성 확인이 끝나기 전(avail=null)에는 저장을 막는다 — 겹치는 시간에 저장을 눌러 두고
    // 서버 에러로 되돌아오는 흐름보다, 잠깐 기다렸다 확실히 누르는 편이 낫다.
    const formValid = Boolean(form?.editable && form.roomId && form.title?.trim() && rangeValid
        && avail?.available);

    const saveForm = async () => {
        const f = form;
        const payload = {
            roomId: f.roomId,
            title: f.title.trim(),
            startAt: toServerDateTime(f.startAt),
            endAt: toServerDateTime(f.endAt),
            // 참석 인원은 보내지 않는다 — 서버가 참석자 명단에서 계산한다.
            memo: f.memo || null,
            participantEmployeeIds: (f.employees || []).map(e => e?.id).filter(v => v != null),
            participantDeptIds: (f.depts || []).map(d => d?.deptId).filter(v => v != null),
        };
        const res = await runApiAction({
            request: () => f.id ? meetingRoomApi.updateReservation(f.id, payload)
                                : meetingRoomApi.createReservation(payload),
            successMsg: f.id ? "예약이 수정되었습니다." : "회의실이 예약되었습니다.",
            setLoading: setSaving, throwOnError: true,
        });
        if (res.ok) { setForm(null); loadReservations(); }
    };

    const deleteForm = async () => {
        if (!form?.id) return;
        if (!(await confirmDialog({ message: "이 회의실 예약을 취소(삭제)하시겠습니까?" }))) return;
        const res = await runApiAction({
            request: () => meetingRoomApi.deleteReservation(form.id),
            successMsg: "예약이 취소되었습니다.", setLoading: setSaving,
        });
        if (res.ok) { setForm(null); loadReservations(); }
    };

    // ─── 달력 셀 배지 ────────────────────────────────────────────
    const renderDayBadges = (day) => {
        const list = byDay.get(day.format(D)) || [];
        if (list.length === 0) return null;
        const shown = list.slice(0, 3);
        return (
            <Stack spacing={0.3} sx={{ minWidth: 0 }}>
                {shown.map(r => {
                    const col = roomColor(r.roomId);
                    return (
                        <Box key={`${r.id}-${day.format(D)}`}
                            onClick={(e) => { e.stopPropagation(); setSelDay(day); openDetail(r); }}
                            title={`${hhmm(r.startAt)}~${hhmm(r.endAt)} ${r.roomName} · ${r.title}`}
                            sx={{
                                px: 0.5, py: 0.1, borderRadius: 0.5, minWidth: 0,
                                bgcolor: col.bg, borderLeft: `3px solid ${col.bar}`, cursor: "pointer",
                                "&:hover": { filter: "brightness(0.96)" },
                            }}>
                            <Typography noWrap sx={{ fontSize: 10.5, fontWeight: 700, color: col.text }}>
                                {hhmm(r.startAt)} {r.title}
                            </Typography>
                        </Box>
                    );
                })}
                {list.length > shown.length && (
                    <Typography sx={{ fontSize: 10, color: C.textMuted, pl: 0.5 }}>
                        +{list.length - shown.length}건
                    </Typography>
                )}
            </Stack>
        );
    };

    // memberCount 를 같이 들고 다녀야 참석 인원을 팀 인원까지 포함해 미리 셀 수 있다.
    const deptOptions = useMemo(() => departments.map(d => ({
        deptId: d.deptId, deptName: d.deptName, memberCount: d.memberCount ?? 0,
    })), [departments]);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: "auto" }}>
            {/* 헤더 */}
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
                <Typography sx={ds.typography.pageTitle}>회의실 예약</Typography>
                <Typography sx={{ ...ds.typography.caption, fontSize: 12.5 }}>
                    날짜를 클릭하면 그 날의 회의실별 시간표가 열립니다
                </Typography>
                <Box sx={{ flex: 1 }} />
                {canManage && (
                    <ActionButton variant="secondary" startIcon={<MeetingRoomRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={() => setRoomAdminOpen(true)} label="회의실 관리" sx={{ fontSize: 12 }} />
                )}
                <ActionButton variant="add" startIcon={<EventAvailableRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => openNew(null, null)} label="예약하기"
                    disabled={rooms.length === 0} />
            </Stack>

            {loading ? <LoadingDots /> : (
                <Box sx={{ display: "flex", gap: 2, alignItems: "stretch", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
                    {/* 좌: 월간 달력 */}
                    <Paper sx={{ ...ds.cardPadded, p: 2, flex: 1, minWidth: 0 }}>
                        <BaseCalendar
                            anchor={anchor} onAnchorChange={onAnchorChange}
                            view="month" supportedViews={["month"]}
                            cellHeight={106}
                            selectedDay={selectedDay}
                            onDayClick={setSelDay}
                            renderDayBadges={renderDayBadges}
                            toolbarRight={
                                <Stack direction="row" gap={0.5} flexWrap="wrap">
                                    {rooms.map(r => {
                                        const col = roomColor(r.id);
                                        return (
                                            <Box key={r.id} sx={{ px: 0.8, py: 0.25, borderRadius: 0.5,
                                                bgcolor: col.bg, borderLeft: `3px solid ${col.bar}` }}>
                                                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: col.text }}>
                                                    {r.name}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            }
                        />
                    </Paper>

                    {/* 우: 선택한 날짜의 회의실별 시간표 */}
                    <Paper sx={{ ...ds.cardPadded, p: 2, width: { xs: "100%", lg: 560 },
                        flexShrink: 0, alignSelf: "stretch",
                        display: { xs: "block", lg: "flex" }, flexDirection: { lg: "column" }, minHeight: 0 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={1.2} flexWrap="wrap">
                            <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                                {selectedDay.format("M월 D일 (ddd)")} 시간표
                            </Typography>
                            <Chip label={`${dayResvs.length}건`} size="small"
                                sx={{ height: 20, fontSize: 11, bgcolor: C.borderLight }} />
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{ fontSize: 11.5, color: C.textMuted }}>
                                빈 칸을 클릭하면 그 시간으로 예약
                            </Typography>
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        {/* lg 에서 좌측 달력과 높이를 맞추고, 시간표는 남는 높이만큼만 차지하며 내부 스크롤한다
                            (비품 예약 화면의 우측 목록과 같은 방식 — absolute 로 흐름에서 빼 높이 순환을 끊는다). */}
                        <Box sx={{ position: { lg: "relative" }, flex: { lg: 1 }, minHeight: { lg: 0 } }}>
                            <Box ref={timelineScrollRef} sx={{ position: { lg: "absolute" }, inset: { lg: 0 },
                                overflowY: "auto", maxHeight: { xs: 620, lg: "none" }, pr: 0.5 }}>
                                {rooms.length === 0 ? (
                                    <Typography sx={{ py: 4, textAlign: "center", fontSize: 13, color: C.textMuted }}>
                                        {canManage
                                            ? "등록된 회의실이 없습니다. [회의실 관리]에서 추가하세요."
                                            : "등록된 회의실이 없습니다."}
                                    </Typography>
                                ) : (
                                    <DayTimeline
                                        day={selectedDay} rooms={rooms} reservations={dayResvs}
                                        roomColor={roomColor} scrollRef={timelineScrollRef}
                                        onSlotClick={(roomId, start) => openNew(roomId, start)}
                                        onBlockClick={openDetail}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* ── 예약 생성 / 상세·수정 ── */}
            <FormModal open={Boolean(form)} onClose={() => setForm(null)}
                title={form?.id ? (form.editable ? "회의실 예약 수정" : "회의실 예약 상세") : "회의실 예약"}
                maxWidth="sm"
                onSave={form?.editable ? saveForm : undefined} saving={saving}
                saveDisabled={!formValid} saveLabel={form?.id ? "저장" : "예약"}
                footerExtras={form?.id && form.editable ? (
                    <ActionButton variant="secondary" onClick={deleteForm} label="예약 취소"
                        sx={{ fontSize: 12, color: C.error }} />
                ) : null}>
                {form && (form.editable ? (
                    <Stack spacing={2}>
                        {/* 관리자는 남의 예약도 고칠 수 있다 — 화면이 내 예약과 똑같으면 남의 회의를
                            무심코 옮기게 된다. 누구 것인지 먼저 알린다. */}
                        {othersReservation && (
                            <Note tone="warn" icon={<ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />}>
                                {form.applicantName || "다른 직원"} 님이 예약한 회의입니다 — 관리자 권한으로 수정 중입니다.
                            </Note>
                        )}
                        <FormField label="회의 제목" required>
                            <TextField fullWidth size="small" value={form.title}
                                placeholder="예: 3분기 킥오프 회의"
                                inputProps={{ maxLength: 200 }}
                                onChange={e => setF({ title: e.target.value })} />
                        </FormField>
                        <FormField label="회의실" required>
                            <ComboBox placeholder="회의실 선택" options={roomOptions}
                                value={form.roomId} onChange={v => setF({ roomId: v })} />
                        </FormField>
                        {/* 날짜·시각을 한 칸(datetime-local)에서 고른다 — 따로 두면 칸이 좁아 값이 잘리고,
                            날짜만 바꾸고 시각을 안 고치는 실수가 잦다.
                            step=600 은 <b>스피너 간격</b>이다 — 화살표·휠은 10분씩 맞물려 움직이고,
                            분 자리를 직접 타이핑하면 10:05 같은 값도 그대로 저장된다(서버가 분 단위까지 받는다). */}
                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                            <FormField label="시작" required sx={{ flex: 1, minWidth: 210 }}>
                                <TextField fullWidth size="small" type="datetime-local" value={form.startAt}
                                    inputProps={{ step: SLOT_MIN * 60 }}
                                    onChange={e => onStartChange(e.target.value)} />
                            </FormField>
                            <FormField label="종료" required sx={{ flex: 1, minWidth: 210 }}>
                                <TextField fullWidth size="small" type="datetime-local" value={form.endAt}
                                    error={Boolean(form.startAt && form.endAt) && !rangeValid}
                                    inputProps={{ step: SLOT_MIN * 60, min: form.startAt }}
                                    onChange={e => setF({ endAt: e.target.value })} />
                            </FormField>
                        </Box>
                        {rangeValid && (
                            <Typography sx={{ fontSize: 11.5, color: C.textMuted, mt: -1 }}>
                                {durationLabel(form.startAt, form.endAt)}
                            </Typography>
                        )}
                        {/* 가용성 — 저장을 누르기 전에 비었는지/무엇과 부딪히는지 알려준다 */}
                        <AvailabilityNote rangeValid={rangeValid} avail={avail} hasRoom={Boolean(form.roomId)} />
                        <Divider textAlign="left">
                            <Typography sx={{ fontSize: 12, color: C.textMuted }}>참여자</Typography>
                        </Divider>
                        {/* 공통 AppAutocomplete — 아래 «참석자 명단»(EmployeeAutocomplete) 과 같은 2줄 표기로 맞춘다.
                            인원 수는 둘째 줄로 내렸다: 팀을 고르면 참석 인원이 몇 명 늘어나는지 미리 보여야 하는데,
                            이름과 한 줄에 붙이면 타이핑 검색이 "(3명)" 까지 훑어 «3» 만 쳐도 엉뚱한 팀이 걸린다.
                            칩은 팀 이름만 짧게 — 초록색은 개인 참석자(파랑 칩)와 구분하는 신호라 그대로 둔다. */}
                        <FormField label="참여 팀">
                            <AppAutocomplete
                                multiple options={deptOptions}
                                value={form.depts}
                                getOptionLabel={(o) => o?.deptName || ""}
                                secondaryText={(o) => `${o?.memberCount ?? 0}명`}
                                isOptionEqualToValue={(o, v) => String(o?.deptId) === String(v?.deptId)}
                                onChange={(v) => setF({ depts: v })}
                                placeholder="팀 단위로 소집할 팀 선택"
                                renderTag={(o, i, getTagProps) => (
                                    <Chip {...getTagProps({ index: i })} key={o.deptId} label={o.deptName} size="small"
                                        sx={{ fontSize: 11, height: 22, fontWeight: 500,
                                            bgcolor: "#f0fdf4", color: "#15803d",
                                            "& .MuiChip-deleteIcon": { color: "#15803d" } }} />
                                )}
                            />
                        </FormField>
                        <FormField label="참석자 명단">
                            <EmployeeAutocomplete
                                multiple value={form.employees}
                                onChange={(v) => setF({ employees: dedupeById(v) })}
                                excludeIds={(form.employees || []).map(e => e?.id).filter(v => v != null)}
                                placeholder="이름·이메일로 참석자 검색"
                                variant="outlined" disableUnderline={false} />
                        </FormField>
                        {/* 참석 인원은 입력칸이 아니라 명단에서 따라 나오는 값이다 — 직접 적게 두면
                            명단을 고친 뒤에도 숫자가 옛날 값으로 남는다. */}
                        <HeadcountNote count={derivedHeadcount} room={selectedRoom} over={overCapacity}
                            teams={form.depts} people={form.employees} />
                        <FormField label="메모">
                            <TextField fullWidth size="small" multiline rows={2} value={form.memo}
                                placeholder="회의 안건·준비물 등(선택)"
                                onChange={e => setF({ memo: e.target.value })} />
                        </FormField>
                    </Stack>
                ) : (
                    <ReservationDetail form={form} />
                ))}
            </FormModal>

            {/* ── 회의실 관리 (MEETING_ROOM_MANAGE) ── */}
            {/* 관리 모달에는 '사용 안 함' 회의실까지 넘긴다 — 내려 둔 방을 다시 켜려면 목록에 보여야 한다. */}
            <RoomAdminModal open={roomAdminOpen} onClose={() => setRoomAdminOpen(false)}
                rooms={allRooms} onChanged={() => { reloadRooms(); loadReservations(); }} />
        </Box>
    );
}

/** "1시간 30분" 처럼 회의 길이를 사람 말로. 시간을 고른 뒤 길이를 눈으로 확인하는 용도. */
function durationLabel(startAt, endAt) {
    const min = dayjs(endAt).diff(dayjs(startAt), "minute");
    const h = Math.floor(min / 60), m = min % 60;
    const len = h > 0 ? (m > 0 ? `${h}시간 ${m}분` : `${h}시간`) : `${m}분`;
    const sameDay = dayjs(startAt).isSame(dayjs(endAt), "day");
    return sameDay ? `총 ${len}` : `총 ${len} · 날짜를 넘겨 예약됩니다`;
}

/**
 * 참석 인원 — 명단에서 계산된 값을 보여주는 <b>읽기 전용</b> 줄.
 * 팀은 인원 수까지 풀어 적어(기획팀 4명) 왜 그 숫자가 나왔는지 보이게 한다.
 */
function HeadcountNote({ count, room, over, teams, people }) {
    const parts = [
        ...(teams || []).map(t => `${t.deptName} ${Number(t.memberCount) || 0}명`),
        ...((people || []).length ? [`개인 ${people.length}명`] : []),
    ];
    const tone = over ? { bg: C.errorBg, fg: C.error } : { bg: C.borderLight, fg: C.textSecondary };
    return (
        <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap"
            sx={{ px: 1.2, py: 0.8, borderRadius: 1, bgcolor: tone.bg }}>
            <GroupsRoundedIcon sx={{ fontSize: 16, color: tone.fg }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tone.fg }}>
                참석 인원 {count}명
            </Typography>
            {parts.length > 0 && (
                <Typography sx={{ fontSize: 11.5, color: over ? C.error : C.textMuted }}>
                    ({parts.join(" + ")})
                </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 11.5, color: over ? C.error : C.textMuted }}>
                {over ? `'${room.name}' 정원 ${room.capacity}명 초과`
                      : room?.capacity ? `정원 ${room.capacity}명` : "명단에서 자동 계산됩니다"}
            </Typography>
        </Stack>
    );
}

/** id 기준 중복 제거 (선택 순서 유지). */
function dedupeById(list) {
    const seen = new Set();
    const out = [];
    (list || []).forEach((o) => {
        if (!o || typeof o === "string" || o.id == null || seen.has(o.id)) return;
        seen.add(o.id); out.push(o);
    });
    return out;
}

/* ─── 가용성 안내 줄 ─────────────────────────────────────────── */
function AvailabilityNote({ rangeValid, avail, hasRoom }) {
    if (!hasRoom) return null;
    if (!rangeValid) return (
        <Note tone="error" icon={<ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />}>
            종료 시각이 시작 시각보다 뒤여야 합니다.
        </Note>
    );
    if (avail == null) return (
        <Note tone="muted">예약 가능 여부를 확인하는 중…</Note>
    );
    if (avail.available) return (
        <Note tone="success" icon={<CheckCircleRoundedIcon sx={{ fontSize: 15 }} />}>
            이 시간에 예약할 수 있습니다.
        </Note>
    );
    return (
        <Note tone="error" icon={<ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />}>
            이미 예약된 시간입니다 —{" "}
            {(avail.conflicts || []).map(c =>
                `${hhmm(c.startAt)}~${hhmm(c.endAt)} ${c.title}${c.applicantName ? `(${c.applicantName})` : ""}`
            ).join(", ")}
        </Note>
    );
}

function Note({ tone, icon, children }) {
    const map = {
        success: { bg: C.successBg, fg: C.success },
        error:   { bg: C.errorBg,   fg: C.error },
        warn:    { bg: "#fffbeb",   fg: "#b45309" },
        muted:   { bg: C.borderLight, fg: C.textMuted },
    };
    const t = map[tone] || map.muted;
    return (
        <Stack direction="row" alignItems="center" gap={0.6}
            sx={{ px: 1, py: 0.7, borderRadius: 1, bgcolor: t.bg }}>
            {icon && <Box sx={{ color: t.fg, display: "flex" }}>{icon}</Box>}
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: t.fg }}>{children}</Typography>
        </Stack>
    );
}

/* ─── 읽기 전용 상세 (남의 예약) ──────────────────────────────── */
function ReservationDetail({ form }) {
    const row = (label, value) => (
        <Stack direction="row" gap={1.5} sx={{ py: 0.6 }}>
            <Typography sx={{ width: 76, flexShrink: 0, fontSize: 12.5, color: C.textMuted, fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.textPrimary, whiteSpace: "pre-wrap", minWidth: 0 }}>
                {value || "—"}
            </Typography>
        </Stack>
    );
    const sameDay = dayjs(form.startAt).isSame(dayjs(form.endAt), "day");
    const period = dayjs(form.startAt).format("YYYY.MM.DD (ddd) HH:mm")
        + " ~ " + dayjs(form.endAt).format(sameDay ? "HH:mm" : "MM.DD HH:mm")
        + `  (${durationLabel(form.startAt, form.endAt).replace(/^총 /, "")})`;
    return (
        <Stack>
            <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 1 }}>{form.title}</Typography>
            {row("회의실", form.roomName)}
            {row("시간", period)}
            {row("예약자", form.applicantName + (form.deptName ? ` · ${form.deptName}` : ""))}
            {row("참여 팀", (form.depts || []).map(d => d.deptName).join(", "))}
            {row("참석자", [...(form.employees || []).map(e => e.name),
                ...(form.deletedNames || [])].join(", "))}
            {row("참석 인원", form.headcount ? `${form.headcount}명` : "")}
            {row("메모", form.memo)}
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: 11.5, color: C.textMuted }}>
                다른 사람이 예약한 회의라 수정·취소할 수 없습니다. 변경이 필요하면 예약자에게 문의하거나 관리자에게 요청하세요.
            </Typography>
        </Stack>
    );
}

/* ─── 하루 시간표 (회의실 = 열, 시간 = 세로축) ────────────────── */
function DayTimeline({ day, rooms, reservations, roomColor, onSlotClick, onBlockClick, scrollRef }) {
    const now = dayjs();

    // 기본 시간창(08~22시)에서 시작하되, 그 밖의 예약이 있으면 그만큼 넓힌다 —
    // 새벽 행사 준비처럼 창 밖에 잡힌 예약이 화면에서 통째로 사라지지 않도록.
    const [startHour, endHour] = useMemo(() => {
        let sh = DEFAULT_START_HOUR, eh = DEFAULT_END_HOUR;
        for (const r of reservations) {
            const [s, e] = daySpanMinutes(r, day);
            sh = Math.min(sh, Math.floor(s / 60));
            eh = Math.max(eh, Math.ceil(e / 60));
        }
        return [clampHour(sh), clampHour(Math.max(eh, sh + 1))];
    }, [reservations, day]);

    const winStartMin = startHour * 60;
    const totalH = (endHour - startHour) * HOUR_H;
    const hours = [];
    for (let h = startHour; h < endHour; h++) hours.push(h);

    /**
     * 날짜를 바꾸면 <b>볼 만한 곳</b>으로 스크롤을 맞춘다.
     * 시간창이 08~22시라 세로로 길어서, 그냥 두면 맨 위(새벽)만 보이고 정작 오늘 진행 중인 회의나
     * 그 날 첫 예약은 화면 밖에 있다. 오늘이면 '지금', 아니면 첫 예약 시각을 기준으로 잡는다.
     */
    useEffect(() => {
        const el = scrollRef?.current;
        if (!el) return;
        const today = dayjs();
        let focusMin;
        if (day.isSame(today, "day")) focusMin = today.hour() * 60 + today.minute();
        else if (reservations.length) focusMin = Math.min(...reservations.map(r => daySpanMinutes(r, day)[0]));
        else return;   // 볼 예약도 없고 오늘도 아니면 맨 위 그대로가 자연스럽다
        // 기준 시각이 위에서 조금 내려온 자리에 오도록 한 시간쯤 여유를 둔다.
        el.scrollTop = Math.max(0, (focusMin - winStartMin) * PX_PER_MIN - HOUR_H);
    }, [day, startHour, reservations.length]);   // eslint-disable-line react-hooks/exhaustive-deps

    const handleColumnClick = (e, room) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const raw = winStartMin + y / PX_PER_MIN;
        const snapped = Math.round(raw / SLOT_MIN) * SLOT_MIN;
        // 하루를 벗어나지 않게(23:50 을 클릭해도 다음날로 넘어가지 않도록) 마지막 눈금으로 자른다.
        const minute = Math.max(0, Math.min(snapped, 24 * 60 - SLOT_MIN));
        onSlotClick(room.id, day.startOf("day").add(minute, "minute"));
    };

    return (
        <Box sx={{ display: "flex", minWidth: 0 }}>
            {/* 시간 눈금 */}
            <Box sx={{ width: 40, flexShrink: 0, pt: "22px" }}>
                {hours.map(h => (
                    <Box key={h} sx={{ height: HOUR_H, position: "relative" }}>
                        <Typography sx={{ position: "absolute", top: -7, right: 6,
                            fontSize: 10.5, color: C.textMuted, fontWeight: 600 }}>
                            {pad2(h)}:00
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* 회의실 열 */}
            <Box sx={{ flex: 1, display: "flex", gap: 0.5, minWidth: 0 }}>
                {rooms.map(room => {
                    const col = roomColor(room.id);
                    const items = reservations.filter(r => String(r.roomId) === String(room.id));
                    return (
                        <Box key={room.id} sx={{ flex: 1, minWidth: 0 }}>
                            {/* 열 머리 — 회의실 이름 */}
                            <Box sx={{ height: 22, px: 0.5, borderRadius: 0.5, bgcolor: col.bg, mb: 0 }}>
                                <Typography noWrap title={room.name}
                                    sx={{ fontSize: 11, fontWeight: 800, color: col.text, lineHeight: "22px" }}>
                                    {room.name}
                                </Typography>
                            </Box>
                            {/* 눈금 배경 + 예약 블록 */}
                            <Box onClick={(e) => handleColumnClick(e, room)}
                                sx={{
                                    position: "relative", height: totalH, cursor: "pointer",
                                    borderLeft: `1px solid ${C.borderLight}`,
                                    backgroundImage: `repeating-linear-gradient(to bottom, ${C.borderLight} 0px, ${C.borderLight} 1px, transparent 1px, transparent ${HOUR_H}px)`,
                                    "&:hover": { bgcolor: C.rowHover },
                                }}>
                                {items.map(r => {
                                    const [s, e] = daySpanMinutes(r, day);
                                    const top = (s - winStartMin) * PX_PER_MIN;
                                    const h = Math.max((e - s) * PX_PER_MIN, MIN_BLOCK_H);
                                    const past = dayjs(r.endAt).isBefore(now);
                                    const c = past ? PAST_COLOR : col;
                                    return (
                                        <Box key={r.id}
                                            onClick={(ev) => { ev.stopPropagation(); onBlockClick(r); }}
                                            title={`${hhmm(r.startAt)}~${hhmm(r.endAt)} ${r.title} · ${r.applicantName || ""}`}
                                            sx={{
                                                position: "absolute", left: 2, right: 2,
                                                top: Math.max(top, 0),
                                                // 아래로 2px 비우고 테두리를 둘러 '맞닿은 두 회의'가 한 덩어리로 보이지 않게 한다.
                                                // (10:00 에 끝나고 10:00 에 시작하는 연속 회의는 정상이라 자주 생긴다)
                                                height: Math.max(h - 2, MIN_BLOCK_H),
                                                bgcolor: c.bg,
                                                border: `1px solid ${c.bar}40`,
                                                borderLeft: `3px solid ${c.bar}`,
                                                borderRadius: 0.6, px: 0.5, py: 0.1, overflow: "hidden",
                                                "&:hover": { filter: "brightness(0.95)" },
                                            }}>
                                            <Typography noWrap sx={{ fontSize: 10.5, fontWeight: 800, color: c.text }}>
                                                {r.title}
                                            </Typography>
                                            {h >= 30 && (
                                                <Typography noWrap sx={{ fontSize: 9.5, color: c.text, opacity: 0.85 }}>
                                                    {hhmm(r.startAt)}~{hhmm(r.endAt)}
                                                    {r.applicantName ? ` · ${r.applicantName}` : ""}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                                {/* 오늘이면 '지금' 선 — 어느 회의가 진행 중인지 한눈에 */}
                                {day.isSame(now, "day") && (() => {
                                    const min = now.hour() * 60 + now.minute();
                                    if (min < winStartMin || min > endHour * 60) return null;
                                    return (
                                        <Box sx={{ position: "absolute", left: 0, right: 0, pointerEvents: "none",
                                            top: (min - winStartMin) * PX_PER_MIN,
                                            borderTop: `1.5px solid ${C.error}`, opacity: 0.7 }} />
                                    );
                                })()}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

/* ─── 회의실 관리 모달 (MEETING_ROOM_MANAGE) ──────────────────── */
function RoomAdminModal({ open, onClose, rooms, onChanged }) {
    const [draft, setDraft] = useState(emptyRoomDraft());   // 추가 폼
    const [editing, setEditing] = useState(null);           // 인라인 수정 중인 행

    useEffect(() => { if (!open) { setDraft(emptyRoomDraft()); setEditing(null); } }, [open]);

    const payloadOf = (f) => ({
        name: f.name.trim(),
        location: f.location?.trim() || null,
        capacity: f.capacity === "" ? null : Number(f.capacity),
        memo: f.memo?.trim() || null,
        active: f.active,
    });

    const add = async () => {
        if (!draft.name.trim()) return;
        const res = await runApiAction({
            request: () => meetingRoomApi.createRoom(payloadOf(draft)),
            successMsg: "회의실이 추가되었습니다.", throwOnError: true,
        });
        if (res.ok) { setDraft(emptyRoomDraft()); onChanged?.(); }
    };
    const saveEdit = async () => {
        if (!editing?.name.trim()) return;
        const res = await runApiAction({
            request: () => meetingRoomApi.updateRoom(editing.id, payloadOf(editing)),
            successMsg: "회의실이 수정되었습니다.", throwOnError: true,
        });
        if (res.ok) { setEditing(null); onChanged?.(); }
    };
    // 사용 여부 토글은 그 자리에서 바로 반영한다(수정 모드로 들어갔다 나올 일이 아니라서).
    const toggleActive = async (room) => {
        const res = await runApiAction({
            request: () => meetingRoomApi.updateRoom(room.id, payloadOf({ ...room, active: !room.active })),
            successMsg: room.active ? "사용 안 함으로 변경했습니다." : "사용함으로 변경했습니다.",
        });
        if (res.ok) onChanged?.();
    };
    const del = async (room) => {
        if (!(await confirmDialog({ message: `"${room.name}" 회의실을 삭제하시겠습니까?` }))) return;
        const res = await runApiAction({
            request: () => meetingRoomApi.deleteRoom(room.id), successMsg: "삭제되었습니다.",
        });
        if (res.ok) onChanged?.();
    };

    const fields = (f, set) => (
        <Stack direction="row" gap={0.8} sx={{ flex: 1, minWidth: 0 }} flexWrap="wrap">
            <TextField size="small" placeholder="회의실 이름" value={f.name}
                onChange={e => set({ ...f, name: e.target.value })} sx={{ flex: 2, minWidth: 150 }} />
            <TextField size="small" placeholder="위치(예: 4층)" value={f.location}
                onChange={e => set({ ...f, location: e.target.value })} sx={{ flex: 1, minWidth: 96 }} />
            <TextField size="small" type="number" placeholder="정원" value={f.capacity}
                inputProps={{ min: 1 }} sx={{ width: 96 }}
                InputProps={{ endAdornment: <InputAdornment position="end">명</InputAdornment> }}
                onChange={e => set({ ...f, capacity: e.target.value })} />
        </Stack>
    );

    return (
        <FormModal open={open} onClose={onClose} title="회의실 관리" maxWidth="md" cancelLabel="닫기">
            <Stack spacing={1.5}>
                <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                    여기에 등록한 회의공간이 예약 대상이 됩니다. 예약 내역이 있는 회의실은 삭제 대신 '사용 안 함'으로 내려 주세요
                    (지난 예약 기록은 그대로 남고 새 예약에서만 빠집니다).
                </Typography>
                {/* 추가 */}
                <Stack direction="row" gap={1} alignItems="center">
                    {fields(draft, setDraft)}
                    <ActionButton variant="add" onClick={add} label="추가" disabled={!draft.name.trim()} />
                </Stack>
                <Divider />
                {/* 목록 */}
                <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
                    {rooms.length === 0 && (
                        <Typography sx={{ py: 3, textAlign: "center", fontSize: 12.5, color: C.textMuted }}>
                            등록된 회의실이 없습니다.
                        </Typography>
                    )}
                    {rooms.map(room => editing?.id === room.id ? (
                        <Stack key={room.id} direction="row" gap={1} alignItems="center" sx={{ py: 0.8 }}>
                            {fields(editing, setEditing)}
                            <ActionButton variant="add" onClick={saveEdit} label="저장"
                                disabled={!editing.name.trim()} sx={{ fontSize: 12 }} />
                            <ActionButton variant="secondary" onClick={() => setEditing(null)} label="취소"
                                sx={{ fontSize: 12 }} />
                        </Stack>
                    ) : (
                        <Stack key={room.id} direction="row" alignItems="center" gap={1} sx={{ py: 0.8 }}>
                            <Box sx={{ flex: 1, minWidth: 0, opacity: room.active ? 1 : 0.5 }}>
                                <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap">
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{room.name}</Typography>
                                    {room.location && (
                                        <Stack direction="row" alignItems="center" gap={0.2}>
                                            <PlaceRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                                            <Typography sx={{ fontSize: 11.5, color: C.secondary }}>{room.location}</Typography>
                                        </Stack>
                                    )}
                                    {room.capacity && (
                                        <Chip label={`정원 ${room.capacity}명`} size="small"
                                            sx={{ height: 18, fontSize: 10, fontWeight: 700,
                                                bgcolor: C.infoBg, color: C.info }} />
                                    )}
                                    {!room.active && (
                                        <Chip label="사용 안 함" size="small"
                                            sx={{ height: 18, fontSize: 10, fontWeight: 700,
                                                bgcolor: C.borderLight, color: C.textMuted }} />
                                    )}
                                </Stack>
                            </Box>
                            <Tooltip title={room.active ? "사용 안 함으로" : "사용함으로"}>
                                <Switch size="small" checked={room.active} onChange={() => toggleActive(room)} />
                            </Tooltip>
                            <Tooltip title="수정"><IconButton size="small"
                                onClick={() => setEditing({
                                    id: room.id, name: room.name, location: room.location || "",
                                    capacity: room.capacity ?? "", memo: room.memo || "", active: room.active,
                                })}>
                                <EditRoundedIcon sx={{ fontSize: 16, color: C.secondary }} /></IconButton></Tooltip>
                            <Tooltip title="삭제"><IconButton size="small" onClick={() => del(room)}>
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: C.error }} /></IconButton></Tooltip>
                        </Stack>
                    ))}
                </Box>
            </Stack>
        </FormModal>
    );
}
